import React, { useState, useEffect } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Alert,
  Snackbar,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { protectedApi } from '../../services/api';

const MembershipManager = () => {
  const [levels, setLevels] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedLevel, setSelectedLevel] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [formData, setFormData] = useState({
    name: '',
    points_multiplier: 1.0,
    discount_percentage: 0,
    free_shipping_threshold: null,
    points_threshold: 0,
    benefits: []
  });

  const fetchLevels = async () => {
    try {
      console.log('Fetching membership levels...');
      const response = await protectedApi.get('/membership/levels/');
      console.log('Response:', response);
      if (response.data) {
        setLevels(response.data);
      }
    } catch (error) {
      console.error('Error fetching membership levels:', error);
      showSnackbar('Error al cargar los niveles de membresía: ' + (error.detail || error.message), 'error');
    }
  };

  useEffect(() => {
    fetchLevels();
  }, []);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleOpen = (level = null) => {
    if (level) {
      setFormData({
        name: level.name,
        points_multiplier: level.points_multiplier,
        discount_percentage: level.discount_percentage,
        free_shipping_threshold: level.free_shipping_threshold,
        points_threshold: level.points_threshold,
        benefits: level.benefits.map(b => b.description)
      });
      setSelectedLevel(level);
    } else {
      setFormData({
        name: '',
        points_multiplier: 1.0,
        discount_percentage: 0,
        free_shipping_threshold: null,
        points_threshold: 0,
        benefits: ['']
      });
      setSelectedLevel(null);
    }
    setOpenDialog(true);
  };

  const handleClose = () => {
    setOpenDialog(false);
    setSelectedLevel(null);
  };

  const handleSave = async () => {
    try {
      const dataToSend = {
        ...formData,
        free_shipping_threshold: formData.free_shipping_threshold === 0 ? null : formData.free_shipping_threshold,
        points_multiplier: parseFloat(formData.points_multiplier),
        discount_percentage: parseFloat(formData.discount_percentage),
        points_threshold: parseInt(formData.points_threshold)
      };

      let response;
      if (selectedLevel) {
        response = await protectedApi.put(`/membership/levels/${selectedLevel.id}`, dataToSend);
        showSnackbar('Nivel de membresía actualizado correctamente');
      } else {
        response = await protectedApi.post('/membership/levels/', dataToSend);
        showSnackbar('Nivel de membresía creado correctamente');
      }

      // Si la creación fue exitosa, crear los beneficios
      if (response.data && response.data.id) {
        const levelId = response.data.id;
        const benefitPromises = formData.benefits
          .filter(benefit => benefit.trim()) // Filtrar beneficios vacíos
          .map(benefit => 
            protectedApi.post('/membership/benefits/', {
              level_id: levelId,
              description: benefit
            })
          );
        
        await Promise.all(benefitPromises);
      }

      await fetchLevels(); // Recargar los niveles después de guardar
      handleClose();
    } catch (error) {
      console.error('Error saving membership level:', error);
      showSnackbar(
        error.detail || error.message || 'Error al guardar el nivel de membresía',
        'error'
      );
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar este nivel de membresía?')) {
      try {
        await protectedApi.delete(`/membership/levels/${id}`);
        showSnackbar('Nivel de membresía eliminado correctamente');
        fetchLevels();
      } catch (error) {
        console.error('Error deleting membership level:', error);
        showSnackbar('Error al eliminar el nivel de membresía', 'error');
      }
    }
  };

  const handleBenefitChange = (index, value) => {
    const newBenefits = [...formData.benefits];
    newBenefits[index] = value;
    setFormData({ ...formData, benefits: newBenefits });
  };

  const addBenefit = () => {
    setFormData({ ...formData, benefits: [...formData.benefits, ''] });
  };

  const removeBenefit = (index) => {
    const newBenefits = formData.benefits.filter((_, i) => i !== index);
    setFormData({ ...formData, benefits: newBenefits });
  };

  const getLevelColor = (name) => {
    const colors = {
      'GOLD': 'warning',
      'SILVER': 'default',
      'BRONZE': 'primary'
    };
    return colors[name.toUpperCase()] || 'primary';
  };

  return (
    <Box>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">Gestión de Niveles de Membresía</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpen()}
        >
          Nuevo Nivel
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Nivel</TableCell>
              <TableCell>Multiplicador de Puntos</TableCell>
              <TableCell>Descuento (%)</TableCell>
              <TableCell>Envío Gratis desde</TableCell>
              <TableCell>Puntos Necesarios</TableCell>
              <TableCell>Beneficios</TableCell>
              <TableCell>Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {levels.map((level) => (
              <TableRow key={level.id}>
                <TableCell>
                  <Chip
                    label={level.name}
                    color={getLevelColor(level.name)}
                  />
                </TableCell>
                <TableCell>{level.points_multiplier}x</TableCell>
                <TableCell>{level.discount_percentage}%</TableCell>
                <TableCell>
                  {level.free_shipping_threshold === null
                    ? 'Siempre'
                    : `$${level.free_shipping_threshold}`}
                </TableCell>
                <TableCell>{level.points_threshold}</TableCell>
                <TableCell>
                  <List dense>
                    {level.benefits.map((benefit, index) => (
                      <ListItem key={index}>
                        <ListItemText
                          primary={benefit.description}
                          sx={{ margin: 0 }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </TableCell>
                <TableCell>
                  <IconButton onClick={() => handleOpen(level)} size="small">
                    <EditIcon />
                  </IconButton>
                  <IconButton onClick={() => handleDelete(level.id)} size="small">
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openDialog} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedLevel ? 'Editar Nivel' : 'Nuevo Nivel'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Nombre del Nivel"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
            <TextField
              label="Multiplicador de Puntos"
              type="number"
              inputProps={{ step: 0.1, min: 1 }}
              value={formData.points_multiplier}
              onChange={(e) => setFormData({ ...formData, points_multiplier: parseFloat(e.target.value) })}
              required
              helperText="Mínimo 1.0"
            />
            <TextField
              label="Porcentaje de Descuento"
              type="number"
              inputProps={{ min: 0, max: 100 }}
              value={formData.discount_percentage}
              onChange={(e) => setFormData({ ...formData, discount_percentage: parseFloat(e.target.value) })}
              required
              helperText="Entre 0 y 100"
            />
            <TextField
              label="Umbral de Envío Gratis"
              type="number"
              value={formData.free_shipping_threshold}
              onChange={(e) => setFormData({ ...formData, free_shipping_threshold: parseFloat(e.target.value) })}
              helperText="Dejar en 0 para envío gratis siempre"
            />
            <TextField
              label="Puntos Necesarios"
              type="number"
              inputProps={{ min: 0 }}
              value={formData.points_threshold}
              onChange={(e) => setFormData({ ...formData, points_threshold: parseInt(e.target.value) })}
              required
              helperText="Puntos necesarios para alcanzar este nivel"
            />

            <Typography variant="h6" sx={{ mt: 2 }}>Beneficios</Typography>
            {formData.benefits.map((benefit, index) => (
              <Box key={index} sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  fullWidth
                  label={`Beneficio ${index + 1}`}
                  value={benefit}
                  onChange={(e) => handleBenefitChange(index, e.target.value)}
                />
                <IconButton
                  onClick={() => removeBenefit(index)}
                  disabled={formData.benefits.length === 1}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            ))}
            <Button startIcon={<AddIcon />} onClick={addBenefit}>
              Agregar Beneficio
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancelar</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!formData.name || formData.points_multiplier < 1}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MembershipManager;
